import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';

import {getTheme} from '@ey/styled-theme';

import Utility from '../../../../../../util/utilityfunctions';
import {labels, currentResource} from '../../../../../../util/utils';
import normalizeDashboardFilters from '../../../../../../util/dashboard/normalizeDashboardFilters';
import {
	auditPhases,
	milestonesFilterId,
	projectSessionKeys,
	dashboardTabId,
	dashboardChartColors
} from '../../../../../../util/uiconstants';

import Tile from '../../../../../Common/Tile';
import BarChart from '../../../../../Common/Charts/BarChart';
import {loadStorage, saveStorage} from '../../../../../Common/localStorage';

import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../../actions/Metrics/dashboardFiltersAction';
import Select from '../../../../../CoreWrappers/Select';

const MilestonesSummaryView = ({milestonesData}) => {
	const dispatch = useDispatch();
	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.milestones}`;
	const currentTheme = getTheme(Utility.isDarkMode());
	const currentState = loadStorage(psessionKEy);

	const [series, setSeries] = useState([]);
	const [categories, setCategories] = useState([]);
	const [milestoneSwitcher, setMilestoneSwitcher] = useState(
		currentState?.milestoneSwitcherFilterid || auditPhases.scopeAndStrategy
	);

	useEffect(() => {
		if (currentState && currentState.milestoneSwitcherFilterid) {
			setMilestoneSwitcher(currentState.milestoneSwitcherFilterid);
			loadSeries(currentState.milestoneSwitcherFilterid);
		}
	}, []);

	useEffect(() => {
		if (!milestoneSwitcher) {
			setMilestoneSwitcher(auditPhases.scopeAndStrategy);
			loadSeries(auditPhases.scopeAndStrategy);
		} else {
			loadSeries(milestoneSwitcher);
		}
	}, [milestonesData]);

	const getEngagementsCountBucket = (min, max, milestoneId) => {
		return (
			milestoneId &&
			milestonesData.filter((item) => {
				const bucket = item?.pCG?.replace(/\s/g, '');
				return item.tPTId === milestoneId && (bucket === min || bucket === max);
			})?.length
		);
	};

	const getTotalTasks = (milestoneId) => {
		return _.sumBy(
			milestonesData.filter((item) => item.tPTId === milestoneId),
			'tT'
		);
	};

	const loadMilestones = (auditPhaseId) => {
		let data = {};

		if (milestonesData) {
			let milestones = milestonesData.filter((item) => item.aPId === auditPhaseId);

			if (milestones) {
				data = _.uniqBy(milestones, 'tPTId').map((item) => {
					if (item)
						return {
							milestoneId: item.tPTId,
							milestoneName: currentResource.milestones.find((milestone) => milestone.id === item.tPTId).label,
							totalTasks: getTotalTasks(item.tPTId),
							buckets: [
								getEngagementsCountBucket('0%', '0%', item.tPTId),
								getEngagementsCountBucket('1-24%', '25-49%', item.tPTId),
								getEngagementsCountBucket('50-64%', '65-79%', item.tPTId),
								getEngagementsCountBucket('80-100%', '80-100%', item.tPTId)
							]
						};
				});
			}
		}

		return data;
	};

	const loadSeries = (auditPhaseId) => {
		const seriesToAdd = loadMilestones(auditPhaseId);

		const milestoneBuckets = [
			{name: '0%', color: dashboardChartColors.blue[0]},
			{name: '1-49%', color: dashboardChartColors.blue[1]},
			{name: '50-79%', color: dashboardChartColors.blue[2]},
			{name: '>80%', color: dashboardChartColors.blue[3]}
		];

		setSeries(
			milestoneBuckets.map((bucket, index) => ({
				name: bucket.name,
				data: seriesToAdd.map((item) => item.buckets[index]),
				color: bucket.color
			}))
		);

		setCategories(seriesToAdd.map((item) => [item.milestoneName, item.totalTasks]));
	};

	const handlerOnMilestoneChange = (id) => {
		const _id = Number.parseInt(id);

		setMilestoneSwitcher(_id);
		loadSeries(_id);

		let currentState = loadStorage(psessionKEy);
		if (!currentState) currentState = {};

		currentState.milestoneSwitcherFilterid = _id;
		saveStorage(psessionKEy, currentState);
	};

	const handlerOnGraphicClick = (highChartEvent) => {
		const highChartCategory = highChartEvent.point?.category;
		const milestoneName = highChartCategory[0] ?? '';

		const getMilestoneRanges = (color) => {
			//Dark and light code colors are the same.
			switch (color) {
				case dashboardChartColors.blue[0]:
					return ['0%', '0%', '0%'];
				case dashboardChartColors.blue[1]:
					return ['1-24%', '25-49%', '1-49%'];
				case dashboardChartColors.blue[2]:
					return ['50-64%', '65-79%', '50-79%'];
				case dashboardChartColors.blue[3]:
					return ['80-100%', '80-100%', '>80%'];
			}
		};

		const getFilteredEngagemets = (min, max) => {
			const getMilestoneId = (name) => {
				return currentResource.milestones.find((milestone) => milestone.label === name)?.id;
			};

			const milestoneId = getMilestoneId(milestoneName);

			const engagements = milestonesData?.filter((item) => {
				const bucket = item?.pCG?.replace(/\s/g, '');
				return item.tPTId === milestoneId && (bucket === min || bucket === max);
			});

			return engagements.map(({engId}) => engId);
		};

		const handlerOnClearFilter = () => {
			dispatch(clearFilterDashboardTiles(dashboardTabId.milestones));
		};

		const [mi, ma, range] = getMilestoneRanges(highChartEvent.point?.color?.trim()?.toLowerCase());

		const getChips = () => {
			return [
				{
					serieLabel: range,
					categoryLabel: milestoneName,
					type: milestonesFilterId.milestones,
					onClearChip: handlerOnClearFilter
				}
			];
		};

		const dashboardFilters = normalizeDashboardFilters(getChips(), getFilteredEngagemets(mi, ma), handlerOnClearFilter);

		dispatch(updateFilterDashboardTiles(dashboardTabId.milestones, dashboardFilters));
	};

	const renderHeader = () => {
		const props = {
			value: milestoneSwitcher,
			options: currentResource.auditPhases,
			onChange: handlerOnMilestoneChange,
			wrapperClass: 'card-header-select-size'
		};

		return <Select {...props} />;
	};

	const renderBarChart = () => {
		const barChartProps = {
			series: series,
			categories: categories,
			title: labels.engagementCount,
			onGraphicClick: handlerOnGraphicClick
		};

		return <BarChart {...barChartProps} />;
	};

	const tileProps = {
		title: labels.taskCompletionByMilestone,
		subTitle: labels.basedOnCompletedTasksForEachEngagement,
		header: renderHeader(),
		className: 'full',
		isEmpty: categories.length === 0,
		emptyTitle: labels.noMilestoneDataForSelection,
		emptySubtitle: labels.selectDifferentAuditPhase
	};

	return <Tile {...tileProps}>{renderBarChart()}</Tile>;
};

MilestonesSummaryView.defaultProps = {
	milestonesData: []
};

MilestonesSummaryView.propTypes = {
	milestonesData: PropTypes.arrayOf(
		PropTypes.shape({
			aPId: PropTypes.number,
			dO: PropTypes.number,
			eD: PropTypes.string,
			engKey: PropTypes.number,
			pC: PropTypes.number,
			pCG: PropTypes.string,
			pNO: PropTypes.number,
			pNOP: PropTypes.string,
			sD: PropTypes.string,
			tC: PropTypes.number,
			tIP: PropTypes.number,
			tIPOD: PropTypes.number,
			tIR: PropTypes.number,
			tIROD: PropTypes.number,
			tNOD: PropTypes.number,
			tO: PropTypes.number,
			tPTId: PropTypes.number,
			tT: PropTypes.number
		})
	)
};

export default MilestonesSummaryView;
